import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Sparkles,
  Loader,
  Star,
  Clock,
  TrendingUp,
  ArrowRight,
  Users,
  Zap,
  CheckCircle,
  Headphones,
  BarChart3,
  Mail,
  Code,
  Briefcase,
  DollarSign,
  Target,
  PenTool,
  Settings
} from "lucide-react";
import BackToDemoButton from "../components/BackToDemoButton";

// Category icons mapping
const categoryIcons = {
  "Customer Support": Headphones,
  "Sales & CRM": Target,
  "Marketing": Mail,
  "Data Analytics": BarChart3,
  "Content Creation": PenTool,
  "HR & Recruiting": Users,
  "Finance": DollarSign,
  "Operations": Settings,
  "Development": Code,
};

// Sample AI services for demo showcase
const featuredServices = [
  {
    id: 1,
    name: "ChatBot Pro",
    tagline: "24/7 AI Customer Support",
    category: "Customer Support",
    price: "From $49/mo",
    setupTime: "1 day setup",
    highlight: "Multi-channel"
  },
  {
    id: 2,
    name: "LeadGenius AI",
    tagline: "Smart Sales Automation",
    category: "Sales & CRM",
    price: "From $149/mo",
    setupTime: "3 day setup",
    highlight: "CRM Integration"
  },
  {
    id: 3,
    name: "ContentMaster",
    tagline: "AI Content Generator",
    category: "Content Creation",
    price: "From $79/mo",
    setupTime: "Same day",
    highlight: "SEO Optimized"
  },
  {
    id: 4,
    name: "DataVision Pro",
    tagline: "Predictive Business Analytics",
    category: "Data Analytics",
    price: "From $199/mo",
    setupTime: "1 week setup",
    highlight: "Real-time insights"
  }
];

// Categories for browsing
const categories = [
  { name: "Customer Support", icon: Headphones, count: 25, color: "bg-blue-500" },
  { name: "Sales & CRM", icon: Target, count: 18, color: "bg-green-500" },
  { name: "Marketing", icon: Mail, count: 32, color: "bg-purple-500" },
  { name: "Data Analytics", icon: BarChart3, count: 21, color: "bg-orange-500" },
  { name: "Content Creation", icon: PenTool, count: 15, color: "bg-pink-500" },
  { name: "HR & Recruiting", icon: Users, count: 12, color: "bg-indigo-500" },
  { name: "Finance", icon: DollarSign, count: 14, color: "bg-emerald-500" },
  { name: "Operations", icon: Settings, count: 19, color: "bg-amber-500" },
];

export default function HomePage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setTimeout(() => {
      navigate('/results', { 
        state: { 
          query: searchQuery,
          fromAssessment: false 
        }
      });
      setIsSearching(false);
    }, 500);
  };

  const handleCategoryClick = (categoryName) => {
    navigate('/browse', { state: { category: categoryName } });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#121212]">
      <BackToDemoButton />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-[#121212]/95 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex justify-between items-center h-16">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <img src="/logo.svg" alt="Logo" className="w-10 h-10" />
            <span className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
              AI Fit Engine
            </span>
          </button>
          <div className="flex items-center gap-2 sm:gap-6">
            <button
              onClick={() => navigate('/browse')}
              className="px-3 sm:px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-xs sm:text-sm font-medium transition-colors"
            >
              Browse
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="px-3 sm:px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-xs sm:text-sm font-medium transition-colors hidden sm:block"
            >
              Pricing
            </button>
            <button
              onClick={() => navigate('/list-product')}
              className="px-3 sm:px-4 py-2 text-orange-500 hover:text-orange-600 text-xs sm:text-sm font-medium transition-colors"
            >
              List Your AI
            </button>
            <button
              onClick={() => navigate('/assessment')}
              className="px-4 sm:px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors text-sm"
            >
              Find AI
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section - Marketplace Style */}
      <section className="pt-24 sm:pt-28 pb-12 sm:pb-16 bg-gradient-to-b from-gray-50 to-white dark:from-[#1E1E1E] dark:to-[#121212]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="text-center space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 dark:bg-orange-900/30 rounded-full">
              <Sparkles className="text-orange-600 dark:text-orange-400" size={16} />
              <span className="text-orange-600 dark:text-orange-400 text-sm font-medium">
                AI-Powered Matching Engine
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
              Find the Perfect AI <br className="hidden sm:block" />
              <span className="text-orange-500">For Your Business</span>
            </h1>

            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-2xl mx-auto">
              Discover, compare, and connect with AI solutions tailored to your needs.
              Get matched with vetted providers and start transforming your business.
            </p>

            {/* Search Box */}
            <div className="pt-4 max-w-3xl mx-auto">
              <div className="bg-white dark:bg-[#262626] rounded-2xl shadow-2xl dark:shadow-none dark:ring-1 dark:ring-gray-700 p-2">
                <form onSubmit={handleSearch} className="flex gap-2">
                  <div className="flex-1 relative">
                    <Search
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                      size={20}
                    />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Try: customer support, sales automation, content creation..."
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-[#1E1E1E] border-2 border-transparent focus:border-orange-500 rounded-xl outline-none text-gray-900 dark:text-gray-100 text-sm sm:text-base"
                      disabled={isSearching}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSearching || !searchQuery.trim()}
                    className="px-6 sm:px-8 py-4 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white rounded-xl font-medium transition-colors flex items-center gap-2 disabled:cursor-not-allowed"
                  >
                    {isSearching ? (
                      <Loader className="animate-spin" size={20} />
                    ) : (
                      <Search size={20} />
                    )}
                    <span className="hidden sm:inline">Search</span>
                  </button>
                </form>
              </div>

              {/* Popular Categories Tags */}
              <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
                <span className="text-sm text-gray-500">Popular:</span>
                {["Customer Support", "Sales AI", "Marketing Tools", "Data Analytics"].map((tag) => (
                  <button
                    key={tag}
                    onClick={() => navigate('/browse', { state: { category: tag } })}
                    className="text-sm text-orange-600 hover:text-orange-700 hover:underline font-medium"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Value Props */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 max-w-4xl mx-auto pt-8">
              <div className="text-center p-4 bg-white dark:bg-[#1E1E1E] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Zap className="text-orange-500" size={20} />
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Quick Setup</p>
                <p className="text-xs text-gray-500 mt-1">Days, not months</p>
              </div>
              <div className="text-center p-4 bg-white dark:bg-[#1E1E1E] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-2">
                  <CheckCircle className="text-green-500" size={20} />
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Vetted Providers</p>
                <p className="text-xs text-gray-500 mt-1">Quality assured</p>
              </div>
              <div className="text-center p-4 bg-white dark:bg-[#1E1E1E] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Search className="text-blue-500" size={20} />
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Smart Matching</p>
                <p className="text-xs text-gray-500 mt-1">AI-powered search</p>
              </div>
              <div className="text-center p-4 bg-white dark:bg-[#1E1E1E] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Users className="text-purple-500" size={20} />
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Direct Connect</p>
                <p className="text-xs text-gray-500 mt-1">No middlemen</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Browse by Category */}
      <section className="py-12 sm:py-16 bg-white dark:bg-[#121212]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
                Browse AI Services by Category
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mt-2">Find the perfect AI solution for your needs</p>
            </div>
            <button
              onClick={() => navigate('/browse')}
              className="hidden sm:flex items-center gap-2 text-orange-500 hover:text-orange-600 font-medium"
            >
              View All <ArrowRight size={18} />
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.map((category) => {
              const IconComponent = category.icon;
              return (
                <button
                  key={category.name}
                  onClick={() => handleCategoryClick(category.name)}
                  className="group p-6 bg-gray-50 dark:bg-[#1E1E1E] rounded-xl hover:shadow-lg hover:-translate-y-1 transition-all border border-gray-100 dark:border-gray-800 text-left"
                >
                  <div className={`w-12 h-12 ${category.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <IconComponent className="text-white" size={24} />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    {category.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {category.count} services
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured AI Services */}
      <section className="py-12 sm:py-16 bg-gray-50 dark:bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
                Featured AI Solutions
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">Explore what's possible with AI</p>
            </div>
            <button
              onClick={() => navigate('/browse')}
              className="hidden sm:flex items-center gap-2 text-orange-500 hover:text-orange-600 font-medium"
            >
              Browse All <ArrowRight size={18} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredServices.map((service) => (
              <div
                key={service.id}
                onClick={() => navigate('/results', { state: { query: service.category } })}
                className="group bg-white dark:bg-[#1E1E1E] rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all cursor-pointer border border-gray-100 dark:border-gray-800"
              >
                {/* Service Header */}
                <div className="p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
                      {service.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 dark:text-gray-100 group-hover:text-orange-500 transition-colors">
                        {service.name}
                      </h3>
                      <p className="text-sm text-gray-500">{service.category}</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                    {service.tagline}
                  </p>
                  
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1 text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2 py-1 rounded-full">
                      <Clock size={10} />
                      {service.setupTime}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 px-2 py-1 rounded-full">
                      {service.highlight}
                    </span>
                  </div>
                </div>

                {/* Price Footer */}
                <div className="px-5 py-3 bg-gray-50 dark:bg-[#262626] border-t border-gray-100 dark:border-gray-700">
                  <span className="font-semibold text-gray-900 dark:text-gray-100">{service.price}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Browse Button */}
          <div className="mt-8 text-center sm:hidden">
            <button
              onClick={() => navigate('/browse')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium"
            >
              Browse All Solutions <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 sm:py-16 bg-white dark:bg-[#121212]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              How AI Fit Engine Works
            </h2>
            <p className="text-gray-600 dark:text-gray-400">From discovery to implementation in 3 simple steps</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto">
                <span className="text-2xl font-bold text-orange-600 dark:text-orange-400">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Browse & Compare
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Search 150+ AI services. Compare prices, reviews, and features side-by-side.
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto">
                <span className="text-2xl font-bold text-orange-600 dark:text-orange-400">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Book a Demo
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Connect directly with AI providers. See the product in action before committing.
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto">
                <span className="text-2xl font-bold text-orange-600 dark:text-orange-400">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Start & Scale
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Get set up in days, not months. AI scales with your business automatically.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why AI Fit Engine */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-gray-900 to-gray-800 dark:from-[#0A0A0A] dark:to-[#121212]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              Why Businesses Choose AI Fit Engine
            </h2>
            <p className="text-gray-400">The smarter way to find AI solutions</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { 
                icon: Clock, 
                title: "Save Time", 
                description: "Stop spending weeks researching AI tools. Our matching engine surfaces the most relevant solutions for your specific needs in seconds."
              },
              { 
                icon: CheckCircle, 
                title: "Reduce Risk", 
                description: "Every provider is reviewed and verified. See real features, pricing, and setup requirements before you commit to anything."
              },
              { 
                icon: Zap, 
                title: "Move Fast", 
                description: "Connect directly with AI providers. Book demos, ask questions, and get started without the usual sales runaround."
              },
            ].map((item, i) => (
              <div key={i} className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
                <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center mb-4">
                  <item.icon className="text-orange-400" size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For AI Startups CTA */}
      <section className="py-12 sm:py-16 bg-orange-50 dark:bg-orange-900/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 dark:bg-orange-900/30 rounded-full mb-6">
            <Zap className="text-orange-600" size={16} />
            <span className="text-orange-600 dark:text-orange-400 text-sm font-medium">For AI Startups</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Get Your AI Solution Discovered
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            Join our curated directory of AI providers. Connect with businesses actively searching for solutions like yours.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/list-product')}
              className="px-8 py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-all flex items-center justify-center gap-2"
            >
              List Your Service
              <ArrowRight size={18} />
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="px-8 py-4 border-2 border-gray-900 dark:border-white text-gray-900 dark:text-white rounded-xl font-medium hover:bg-gray-100 dark:hover:bg-white/10 transition-all"
            >
              View Pricing
            </button>
          </div>

          {/* Benefits */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 text-left max-w-3xl mx-auto">
            {[
              { icon: CheckCircle, text: "Free basic listing" },
              { icon: Users, text: "Connect with businesses" },
              { icon: TrendingUp, text: "Track your leads" },
            ].map((benefit, i) => (
              <div key={i} className="flex items-center gap-3 p-4 bg-white dark:bg-[#1E1E1E] rounded-lg">
                <benefit.icon className="text-green-500" size={20} />
                <span className="text-gray-700 dark:text-gray-300 font-medium">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-white dark:bg-[#121212] border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="Logo" className="w-8 h-8" />
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                © 2025 AI Fit Engine. All rights reserved.
              </p>
            </div>
            <div className="flex gap-6">
              <button onClick={() => navigate('/browse')} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-sm">
                Browse
              </button>
              <button onClick={() => navigate('/about')} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-sm">
                About
              </button>
              <button onClick={() => navigate('/pricing')} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-sm">
                Pricing
              </button>
              <button onClick={() => navigate('/list-product')} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-sm">
                List Your AI
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
